import * as React from 'react';
import { Suspense } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Button,
  Badge,
  Label,
} from '@forgenetwork/ui';
import { useToast } from '@/shared/hooks/useToast';
import { api } from '@/lib/api';
import {
  CheckCircle,
  XCircle,
  AlertCircle,
  Edit,
} from 'lucide-react';
import { AdminLayout } from '@/components/admin/AdminLayout';

export const Route = createFileRoute('/admin/subscriptions')({
  component: () => (
    <ErrorBoundary>
      <Suspense fallback={<AdminSubscriptionsSkeleton />}>
        <AdminSubscriptionsPage />
      </Suspense>
    </ErrorBoundary>
  ),
});

type SubscriptionStatus = 'active' | 'canceled' | 'past_due' | 'trialing';

// Plan pricing in cents (monthly)
const PLAN_PRICING: Record<string, number> = {
  free: 0,
  basic: 1900, // $19/month
  pro: 4900, // $49/month
  enterprise: 19900, // $199/month
};

function getPlanPrice(planId: string): number {
  const normalizedPlan = planId.toLowerCase();
  return PLAN_PRICING[normalizedPlan] ?? 0;
}

interface Subscription {
  id: string;
  userId: string;
  userName: string;
  userEmail: string;
  plan: string;
  status: SubscriptionStatus;
  currentPeriodStart: string;
  currentPeriodEnd: string;
  cancelAtPeriodEnd: boolean;
  amount: number;
}

function AdminSubscriptionsPage(): React.JSX.Element {
  const [page, setPage] = React.useState(1);
  const [statusFilter, setStatusFilter] = React.useState<string>('all');
  const [planFilter, setPlanFilter] = React.useState<string>('all');
  const [overrideDialogOpen, setOverrideDialogOpen] = React.useState(false);
  const [selectedSubscription, setSelectedSubscription] = React.useState<Subscription | null>(null);
  const [newPlan, setNewPlan] = React.useState('');
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const pageSize = 20;

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'subscriptions', page, statusFilter, planFilter],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: pageSize.toString(),
        ...(statusFilter !== 'all' && { status: statusFilter }),
        ...(planFilter !== 'all' && { plan: planFilter }),
      });

      const response = await api.get<{
        subscriptions: Array<{
          id: string;
          userId: string;
          stripeSubscriptionId: string;
          status: SubscriptionStatus;
          planId: string;
          currentPeriodStart: string;
          currentPeriodEnd: string;
          cancelAtPeriodEnd: boolean;
          createdAt: string;
          updatedAt: string;
          user: {
            id: string;
            email: string;
            name: string;
          };
        }>;
        pagination: {
          page: number;
          pageSize: number;
          total: number;
          totalPages: number;
        };
      }>(`/admin/subscriptions?${params}`);
      
      if (!response || typeof response !== 'object') {
        throw new Error('Invalid response format from subscriptions API');
      }
      if (!Array.isArray(response.subscriptions)) {
        throw new Error('Subscriptions data is not an array');
      }
      
      // Transform response to match expected format
      return {
        subscriptions: response.subscriptions.map((sub) => ({
          id: sub.id,
          userId: sub.userId,
          userName: sub.user?.name ?? 'Unknown',
          userEmail: sub.user?.email ?? 'unknown@example.com',
          plan: sub.planId,
          status: sub.status,
          currentPeriodStart: sub.currentPeriodStart,
          currentPeriodEnd: sub.currentPeriodEnd,
          cancelAtPeriodEnd: sub.cancelAtPeriodEnd,
          amount: getPlanPrice(sub.planId),
        })),
        total: response.pagination?.total ?? 0,
        page: response.pagination?.page ?? page,
        pageSize: response.pagination?.pageSize ?? pageSize,
        totalPages: response.pagination?.totalPages ?? 1,
      };
    },
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
  });

  const overridePlanMutation = useMutation({
    mutationFn: async ({ subscriptionId, plan }: { subscriptionId: string; plan: string }) => {
      // Backend expects userId, not subscriptionId, and uses different endpoint structure
      // We need to get userId from subscription first
      const subscription = data?.subscriptions.find((s) => s.id === subscriptionId);
      if (!subscription) {
        throw new Error('Subscription not found');
      }
      return api.post(`/admin/subscriptions/${subscription.userId}/override`, {
        planId: plan,
        status: 'active',
        durationDays: 30,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'subscriptions'] });
      setOverrideDialogOpen(false);
      setSelectedSubscription(null);
      setNewPlan('');
      toast({
        title: 'Success',
        description: 'Subscription plan has been updated',
      });
    },
    onError: (error) => {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update plan',
        variant: 'destructive',
      });
    },
  });

  const handleOverridePlan = (subscription: Subscription) => {
    setSelectedSubscription(subscription);
    setNewPlan(subscription.plan);
    setOverrideDialogOpen(true);
  };

  const confirmOverride = () => {
    if (!selectedSubscription || !newPlan) return;
    overridePlanMutation.mutate({
      subscriptionId: selectedSubscription.id,
      plan: newPlan,
    });
  };

  const getStatusBadge = (status: SubscriptionStatus, cancelAtPeriodEnd: boolean) => {
    if (cancelAtPeriodEnd) {
      return (
        <Badge variant="secondary" className="gap-1">
          <AlertCircle className="h-3 w-3" />
          Canceling
        </Badge>
      );
    }

    switch (status) {
      case 'active':
        return (
          <Badge variant="default" className="gap-1 bg-green-500">
            <CheckCircle className="h-3 w-3" />
            Active
          </Badge>
        );
      case 'trialing':
        return (
          <Badge variant="secondary" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Trial
          </Badge>
        );
      case 'past_due':
        return (
          <Badge variant="destructive" className="gap-1">
            <AlertCircle className="h-3 w-3" />
            Past Due
          </Badge>
        );
      case 'canceled':
        return (
          <Badge variant="outline" className="gap-1">
            <XCircle className="h-3 w-3" />
            Canceled
          </Badge>
        );
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getPlanBadge = (plan: string) => {
    const planColors: Record<string, string> = {
      basic: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-100',
      pro: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-100',
      enterprise: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-100',
    };

    const className = planColors[plan.toLowerCase()] || 'bg-gray-100 text-gray-800';

    return (
      <Badge variant="outline" className={className}>
        {plan}
      </Badge>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatAmount = (amount: number) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const activeCount = data?.subscriptions.filter((s) => s.status === 'active').length ?? 0;
  const trialingCount = data?.subscriptions.filter((s) => s.status === 'trialing').length ?? 0;
  const totalRevenue = data?.subscriptions
    .filter((s) => s.status === 'active')
    .reduce((sum, s) => sum + s.amount, 0) ?? 0;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Subscriptions</h1>
          <p className="text-muted-foreground mt-2">
            Manage user subscriptions and billing
          </p>
        </div>

        {/* Stats */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Subscriptions</CardDescription>
              <CardTitle className="text-2xl">
                {isLoading ? '...' : data?.total ?? 0}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active</CardDescription>
              <CardTitle className="text-2xl">
                {isLoading ? '...' : activeCount}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Trialing</CardDescription>
              <CardTitle className="text-2xl">
                {isLoading ? '...' : trialingCount}
              </CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Monthly Revenue</CardDescription>
              <CardTitle className="text-2xl">
                {isLoading ? '...' : formatAmount(totalRevenue)}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle>Filters</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trialing">Trialing</SelectItem>
                  <SelectItem value="past_due">Past Due</SelectItem>
                  <SelectItem value="canceled">Canceled</SelectItem>
                </SelectContent>
              </Select>
              <Select value={planFilter} onValueChange={setPlanFilter}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Plans</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && !isLoading && (
          <Card className="border-destructive">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-destructive">
                <AlertCircle className="h-5 w-5" />
                <div>
                  <p className="font-medium">Failed to load subscriptions</p>
                  <p className="text-sm text-muted-foreground">
                    {error instanceof Error ? error.message : 'An unexpected error occurred'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Subscriptions Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Subscriptions</CardTitle>
            <CardDescription>
              {data?.total
                ? `Showing ${data.subscriptions.length} of ${data.total} subscriptions`
                : 'Loading subscriptions...'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="text-center py-8 text-muted-foreground">
                Loading subscriptions...
              </div>
            ) : error ? (
              <div className="text-center py-8 text-muted-foreground">
                Unable to load subscriptions. Please try again.
              </div>
            ) : (
              <>
                <div className="rounded-md border">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>User</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Period Start</TableHead>
                        <TableHead>Period End</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {data?.subscriptions.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-muted-foreground">
                            No subscriptions found
                          </TableCell>
                        </TableRow>
                      ) : (
                        data?.subscriptions.map((subscription) => (
                          <TableRow key={subscription.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{subscription.userName}</div>
                                <div className="text-xs text-muted-foreground">
                                  {subscription.userEmail}
                                </div>
                              </div>
                            </TableCell>
                            <TableCell>{getPlanBadge(subscription.plan)}</TableCell>
                            <TableCell>
                              {getStatusBadge(subscription.status, subscription.cancelAtPeriodEnd)}
                            </TableCell>
                            <TableCell>{formatAmount(subscription.amount)}</TableCell>
                            <TableCell>{formatDate(subscription.currentPeriodStart)}</TableCell>
                            <TableCell>{formatDate(subscription.currentPeriodEnd)}</TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOverridePlan(subscription)}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Pagination */}
                {data && data.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4">
                    <div className="text-sm text-muted-foreground">
                      Page {page} of {data.totalPages}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page - 1)}
                        disabled={page === 1}
                      >
                        Previous
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(page + 1)}
                        disabled={page === data.totalPages}
                      >
                        Next
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Override Plan Dialog */}
      <Dialog open={overrideDialogOpen} onOpenChange={setOverrideDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Override Subscription Plan</DialogTitle>
            <DialogDescription>
              Change the subscription plan for {selectedSubscription?.userName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="plan">New Plan</Label>
              <Select value={newPlan} onValueChange={setNewPlan}>
                <SelectTrigger id="plan">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="pro">Pro</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="text-sm text-muted-foreground">
              This will immediately change the user's plan and entitlements. Use with caution.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOverrideDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={confirmOverride}
              disabled={overridePlanMutation.isPending || !newPlan}
            >
              {overridePlanMutation.isPending ? 'Updating...' : 'Update Plan'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}

/**
 * Admin Subscriptions Skeleton Loader
 */
function AdminSubscriptionsSkeleton(): React.JSX.Element {
  return (
    <AdminLayout>
      <div className="space-y-6">
        <div>
          <div className="h-9 w-48 bg-muted rounded mb-2" />
          <div className="h-5 w-96 bg-muted rounded" />
        </div>
        <div className="grid gap-4 md:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <div className="h-4 w-32 bg-muted rounded mb-2" />
                <div className="h-8 w-16 bg-muted rounded" />
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
}
